import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';

const ProfileModal = ({ isOpen, onClose, userId, userName }) => {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen && userId) {
            fetchProfile();
        }
    }, [isOpen, userId]);

    const fetchProfile = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.get(`/api/admin/users/${userId}/profile`);
            if (response.ok) {
                const data = await response.json();
                setProfile(data);
            } else {
                setError("Profile not found or could not be loaded.");
            }
        } catch (err) {
            setError("Failed to load profile.");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const overlayStyle = {
        position: 'fixed',
        inset: 0,
        zIndex: 9998,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(18, 60, 46, 0.4)',
        backdropFilter: 'blur(4px)',
    };

    const modalStyle = {
        background: 'white',
        border: '1px solid rgba(18, 60, 46, 0.1)',
        width: '100%',
        maxWidth: '32rem',
        padding: '2rem',
        borderRadius: '1.5rem',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        position: 'relative',
        maxHeight: '90vh',
        overflowY: 'auto',
    };

    const closeBtnStyle = {
        position: 'absolute',
        top: '1.5rem',
        right: '1.5rem',
        color: '#3e7140',
        width: '2.5rem',
        height: '2.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        fontSize: '1.25rem',
        transition: 'all 0.2s',
    };

    const sectionTitleStyle = {
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        color: '#3e7140',
        marginBottom: '0.5rem',
        fontWeight: '700',
        borderBottom: '1px solid #f0eee6',
        paddingBottom: '0.25rem',
    };

    const contentTextStyle = {
        fontSize: '0.95rem',
        color: '#123c2e',
        marginBottom: '1.25rem',
        lineHeight: '1.5',
    };

    return (
        <div style={overlayStyle}>
            <div style={modalStyle}>
                <button
                    onClick={onClose}
                    style={closeBtnStyle}
                    onMouseOver={(e) => e.target.style.background = '#f4f0e6'}
                    onMouseOut={(e) => e.target.style.background = 'transparent'}
                >
                    ✕
                </button>

                <div style={{ marginBottom: '2rem' }}>
                    <h2 style={{ fontSize: '1.75rem', fontFamily: 'serif', color: '#123c2e', marginBottom: '0.25rem' }}>
                        Artisan Profile
                    </h2>
                    <p style={{ color: '#3e7140', fontSize: '0.9rem' }}>Reviewing details for {userName}</p>
                </div>

                {loading && (
                    <div style={{ padding: '2rem', textAlign: 'center' }}>
                        <div className="spinner" style={{ margin: '0 auto 1rem' }}></div>
                        <p style={{ color: '#3e7140' }}>Loading profile...</p>
                    </div>
                )}

                {error && (
                    <div style={{ padding: '1.5rem', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: '1rem', textAlign: 'center' }}>
                        <p>{error}</p>
                        <button onClick={fetchProfile} style={{ marginTop: '1rem', background: 'white', border: '1px solid #fecaca', padding: '0.5rem 1rem', borderRadius: '0.5rem', cursor: 'pointer' }}>
                            Retry
                        </button>
                    </div>
                )}

                {profile && (
                    <div className="animate-fadeIn">
                        <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1.5rem' }}>
                            {profile.profileImageUrl ? (
                                <img
                                    src={profile.profileImageUrl}
                                    alt={profile.displayName}
                                    style={{ width: '80px', height: '80px', borderRadius: '1rem', objectFit: 'cover', border: '2px solid #f0eee6' }}
                                />
                            ) : (
                                <div style={{ width: '80px', height: '80px', background: '#f4f0e6', borderRadius: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem' }}>
                                    👤
                                </div>
                            )}
                            <div>
                                <h3 style={{ fontSize: '1.25rem', color: '#123c2e', marginBottom: '0.25rem' }}>{profile.displayName || userName}</h3>
                                <p style={{ color: '#3e7140', fontSize: '0.85rem' }}>Service Radius: {profile.serviceRadiusKm} km</p>
                            </div>
                        </div>

                        <h4 style={sectionTitleStyle}>About</h4>
                        <p style={contentTextStyle}>{profile.bio || "No bio provided."}</p>

                        <h4 style={sectionTitleStyle}>Skills</h4>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.5rem' }}>
                            {profile.skills && profile.skills.length > 0 ? profile.skills.map(skill => (
                                <span key={skill} style={{
                                    padding: '0.25rem 0.75rem',
                                    background: '#f0eee6',
                                    color: '#123c2e',
                                    borderRadius: '2rem',
                                    fontSize: '0.8rem',
                                    fontWeight: '500'
                                }}>
                                    {skill}
                                </span>
                            )) : <p>None listed.</p>}
                        </div>

                        <h4 style={sectionTitleStyle}>Weekly Schedule</h4>
                        <div style={{ background: '#fffaf0', padding: '1rem', borderRadius: '1rem', border: '1px solid rgba(18, 60, 46, 0.05)' }}>
                            <table style={{ width: '100%', fontSize: '0.85rem' }}>
                                <tbody>
                                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => {
                                        const schedule = profile.weeklySchedule && profile.weeklySchedule[day.toLowerCase()];
                                        return (
                                            <tr key={day} style={{ borderBottom: '1px solid rgba(18, 60, 46, 0.05)' }}>
                                                <td style={{ padding: '0.5rem 0', fontWeight: '600', color: '#3e7140' }}>{day}</td>
                                                <td style={{ padding: '0.5rem 0', textAlign: 'right', color: '#123c2e' }}>
                                                    {schedule && schedule.active ? `${schedule.start} - ${schedule.end}` : "Offline"}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                        onClick={onClose}
                        style={{
                            padding: '0.75rem 2rem',
                            background: '#b48a2b',
                            color: 'white',
                            borderRadius: '2rem',
                            border: 'none',
                            fontWeight: '600',
                            cursor: 'pointer',
                        }}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProfileModal;
